import type { TFunction } from 'i18next';
import { currentLocale } from '../../../lib/format';

/** Number formatting for the dashboard (spec: half-up, CZK, "1,84 mil. Kč" style); unit words come from the catalog. */
export type Fmt = ReturnType<typeof makeFmt>;

export function makeFmt(t: TFunction<['performance', 'common']>) {
  const locale = currentLocale();
  const int = (v: number): string => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(v);
  const dec = (v: number, digits = 1): string =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
  const czk = (v: number | null | undefined): string => (v == null ? '—' : t('dashboard.fmt.czk', { value: int(v) }));
  const compactCzk = (v: number | null | undefined): string => {
    if (v == null) return '—';
    if (Math.abs(v) >= 1e6) return t('dashboard.fmt.millionCzk', { value: dec(v / 1e6, 2) });
    if (Math.abs(v) >= 1e4) return t('dashboard.fmt.thousandCzk', { value: int(v / 1e3) });
    return czk(v);
  };
  const count = (v: number | null | undefined): string => {
    if (v == null) return '—';
    if (Math.abs(v) >= 1e6) return t('dashboard.fmt.million', { value: dec(v / 1e6, 1) });
    if (Math.abs(v) >= 1e4) return t('dashboard.fmt.thousand', { value: int(v / 1e3) });
    return int(v);
  };
  const pct = (share: number | null | undefined, digits = 1): string =>
    share == null ? '—' : `${dec(share * 100, digits)} %`;
  const times = (v: number | null | undefined): string => (v == null ? '—' : `${dec(v, 2)}×`);
  return { locale, int, dec, czk, compactCzk, count, pct, times };
}

/** A calendar day (YYYY-MM-DD) in the active language, e.g. "23. 9. 2026". */
export function fmtDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(currentLocale(), {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
}

/** Bucket label: days as "23. 9.", weeks as their first day, months as "9/2026". */
export function fmtBucket(isoDate: string, bucket: 'day' | 'week' | 'month'): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d, 12));
  if (bucket === 'month') return date.toLocaleDateString(currentLocale(), { month: 'numeric', year: 'numeric' });
  return date.toLocaleDateString(currentLocale(), { day: 'numeric', month: 'numeric' });
}

export interface MetricLike {
  value: number | null;
  status: string;
  reason?: string;
}

/** How a metric's absence is worded: a zero denominator or a not-applicable cost reads differently from a real zero. */
export function metricText(m: MetricLike, render: (v: number) => string): string {
  return m.value == null ? '—' : render(m.value);
}

export type DeltaTone = 'good' | 'bad' | 'neutral';

export interface Delta {
  text: string;
  tone: DeltaTone;
}

/**
 * Change against the comparison period. Amounts and counts as a percentage, ratios in their own
 * unit; `lowerIsBetter` flips the colour for cost metrics. Null when either side is missing.
 */
export function deltaOf(
  current: MetricLike,
  previous: MetricLike | null | undefined,
  fmt: Fmt,
  opts: { lowerIsBetter?: boolean; kind?: 'percent' | 'points' | 'times' } = {},
): Delta | null {
  if (!previous || current.value == null || previous.value == null) return null;
  const kind = opts.kind ?? 'percent';
  let diff: number;
  let text: string;
  if (kind === 'percent') {
    if (previous.value === 0) return null;
    diff = (current.value - previous.value) / Math.abs(previous.value);
    text = `${diff > 0 ? '+' : diff < 0 ? '−' : ''}${fmt.dec(Math.abs(diff) * 100, 1)} %`;
  } else if (kind === 'points') {
    diff = current.value - previous.value;
    text = `${diff > 0 ? '+' : diff < 0 ? '−' : ''}${fmt.dec(Math.abs(diff) * 100, 2)} p. b.`;
  } else {
    diff = current.value - previous.value;
    text = `${diff > 0 ? '+' : diff < 0 ? '−' : ''}${fmt.dec(Math.abs(diff), 2)}×`;
  }
  const improved = opts.lowerIsBetter ? diff < 0 : diff > 0;
  const tone: DeltaTone = Math.abs(diff) < 1e-9 ? 'neutral' : improved ? 'good' : 'bad';
  return { text, tone };
}
